import { List, Map } from 'immutable';
import flatMap from 'lodash/flatMap';
import get from 'lodash/get';
import has from 'lodash/has';
import includes from 'lodash/includes';
import invert from 'lodash/invert';
import map from 'lodash/map';
import memoize from 'lodash/memoize';
import zip from 'lodash/zip';
import { createSelector } from 'reselect';

import CollectionKeys, {
  FilterTypes,
  GET_SORTED_BY_TYPES_ALL,
  GET_SORTED_BY_TYPES_ALL_PREMIUM,
  RECOMMENDATIONS_MAX_LIST_SIZE,
} from './constants';
import * as D from './deps';

export function getCollectionState(state: $TSFixMe): $TSFixMe {
  return state.collection || Map();
}

export function getCollectionItems(state: $TSFixMe): $TSFixMe {
  return getCollectionState(state).getIn([CollectionKeys.COLLECTION, CollectionKeys.ITEMS], List());
}

export function hasMetRecentlyPlayedThreshold(state: $TSFixMe): $TSFixMe {
  return getRecentlyPlayedItemsState(state).getIn(
    [CollectionKeys.HAS_MET_RECENTLY_PLAYED_THRESHOLD],
    false,
  );
}

export function getRecentlyPlayedSPSItems(state: $TSFixMe): $TSFixMe {
  const listenerId = D.getCurrentListenerIdFromState(state);
  return getRecentlyPlayedItemsState(state).getIn(
    [CollectionKeys.RECENTLY_PLAYED_SPS_ITEMS, listenerId],
    List(),
  );
}

export const getRecentlyPlayedPodcasts = createSelector(
  D.getCurrentListenerIdFromState,
  getRecentlyPlayedItemsState,
  (listenerId, recentlyPlayedItems) => {
    return recentlyPlayedItems.getIn([CollectionKeys.RECENTLY_PLAYED_PODCASTS, listenerId], List());
  },
);

export function getRecentlyPlayedItemsState(state: $TSFixMe): $TSFixMe {
  return state.recentlyPlayedItems || Map();
}

export function isRecentlyPlayedSPSEmpty(state: $TSFixMe): $TSFixMe {
  return !getRecentlyPlayedSPSItems(state).size;
}

function _trackNumberComparator(a: $TSFixMe, b: $TSFixMe): $TSFixMe {
  return a - b;
}

// Given a list of tracks IDs, sort them by track number
export function sortTrackIdsByTrackNumber(state: $TSFixMe, trackIds: $TSFixMe): $TSFixMe {
  const allAnnotations = D.getAllAnnotations(state);
  return sortTracksByTrackNumberWithAllAnnotations(allAnnotations, trackIds);
}

function sortTracksByTrackNumberWithAllAnnotations(
  allAnnotations: $TSFixMe,
  tracksOrTrackIds: $TSFixMe,
): $TSFixMe {
  return tracksOrTrackIds.sortBy((trackOrTrackId: $TSFixMe) => {
    const trackId = trackOrTrackId && (trackOrTrackId.pandoraId || trackOrTrackId);
    const annotations = allAnnotations.get(trackId, null);
    return (annotations && annotations.get('trackNumber')) || 0;
  }, _trackNumberComparator);
}

export const getCollectedAlbumTracks = (function (): $TSFixMe {
  let memoizedTracksFilter: $TSFixMe;
  let previousCollection: $TSFixMe;
  let previousAllAnnotations: $TSFixMe;
  return (state: $TSFixMe, albumId: $TSFixMe, sorted = true): $TSFixMe => {
    const collection = getCollectionItems(state);
    const allAnnotations = D.getAllAnnotations(state);
    if (previousCollection !== collection || previousAllAnnotations !== allAnnotations) {
      previousCollection = collection;
      previousAllAnnotations = allAnnotations;
      memoizedTracksFilter = memoize(
        (_albumId: $TSFixMe, _sorted: $TSFixMe) => {
          let filteredTracks = collection.filter((item: $TSFixMe) => {
            return item.albumPandoraId === _albumId;
          });
          if (_sorted) {
            filteredTracks = sortTracksByTrackNumberWithAllAnnotations(
              allAnnotations,
              filteredTracks,
            );
          }
          return filteredTracks.map((item: $TSFixMe) => item.pandoraId);
        },
        (_albumId, _sorted) => _albumId + _sorted,
      );
    }
    return memoizedTracksFilter(albumId, sorted);
  };
})();

/**
 * @param  {object} state - the redux store state
 * @returns {boolean} - whether or not the collection have been loaded into the app
 */
export function collectionHasItems(state: $TSFixMe): $TSFixMe {
  const myCollection = getCollectionItems(state);
  return myCollection.size !== 0;
}

export function isCollectionEmpty(state: $TSFixMe): $TSFixMe {
  return (
    !collectionHasItems(state) && !D.getInDomain('playback.utils.listenerHasStations', false)(state)
  );
}

export function getFilteredCollection(state: $TSFixMe, filterName: $TSFixMe): $TSFixMe {
  return getCollectionState(state).getIn([CollectionKeys.SORTED_FILTERS, filterName], Map());
}

export function getFilteredCollectionItems(state: $TSFixMe, filterName: $TSFixMe): $TSFixMe {
  return getFilteredCollection(state, filterName).get(CollectionKeys.ITEMS, List());
}

export function getFilteredCollectionSort(state: $TSFixMe, filterName: $TSFixMe): $TSFixMe {
  return getFilteredCollection(state, filterName).get(CollectionKeys.SORT);
}

export function collectionHasFilteredTypeItems(state: $TSFixMe, filterName: $TSFixMe): $TSFixMe {
  const pandoraType = getViewFromFilter(filterName);
  return getCollectionItems(state).some((item: $TSFixMe) => {
    return D.getPandoraTypeFromPandoraId(item.pandoraId) === pandoraType;
  });
}

export function isPodcastFilterType(filterName: $TSFixMe): $TSFixMe {
  return filterName === FilterTypes.PODCASTS || filterName === FilterTypes.EPISODES;
}

export function getFilterFromTypePrefixes(typePrefixes: $TSFixMe): $TSFixMe {
  if (
    // @ts-expect-error TS(2556) FIXME: A spread argument must either have a tuple type or... Remove this comment to see the full error message
    includes(typePrefixes, ...GET_SORTED_BY_TYPES_ALL) ||
    // @ts-expect-error TS(2556) FIXME: A spread argument must either have a tuple type or... Remove this comment to see the full error message
    includes(typePrefixes, ...GET_SORTED_BY_TYPES_ALL_PREMIUM)
  ) {
    return FilterTypes.ALL;
  }
  if (includes(typePrefixes, D.PandoraTypes.ARTIST)) return FilterTypes.ARTISTS;
  if (includes(typePrefixes, D.PandoraTypes.ALBUM)) return FilterTypes.ALBUMS;
  if (includes(typePrefixes, D.PandoraTypes.SONG)) return FilterTypes.SONGS;
  if (includes(typePrefixes, D.PandoraTypes.PODCAST)) return FilterTypes.PODCASTS;
  if (includes(typePrefixes, D.PandoraTypes.EPISODE)) return FilterTypes.EPISODES;
}

function filterHasCorrectSort(state: $TSFixMe, filter: $TSFixMe, sortOrder: $TSFixMe): $TSFixMe {
  return !sortOrder || getFilteredCollectionSort(state, filter) === sortOrder;
}

export function getSongPandoraIds(state: $TSFixMe, sortOrder: $TSFixMe): $TSFixMe {
  const tracks = filterHasCorrectSort(state, FilterTypes.SONGS, sortOrder)
    ? getFilteredCollectionItems(state, FilterTypes.SONGS)
    : List();
  const ids: $TSFixMe = [];
  tracks.forEach((track: $TSFixMe) => {
    if (track && track.pandoraId) {
      ids.push(track.pandoraId);
    } else {
      ids.push(null);
    }
  });
  return List(ids);
}

export function getIndexOfTrack(state: $TSFixMe, id: $TSFixMe): $TSFixMe {
  const tracks = getFilteredCollectionItems(state, FilterTypes.SONGS);
  if (tracks) {
    return tracks.findIndex((track: $TSFixMe) => track && track.pandoraId === id);
  }
}

export function getFetchedOffsetList(
  state: $TSFixMe,
  filterName: $TSFixMe,
  sortOrder: $TSFixMe,
): $TSFixMe {
  if (!filterHasCorrectSort(state, filterName, sortOrder)) return List();
  return getFilteredCollection(state, filterName)
    .get(CollectionKeys.FETCHED_OFFSETS, List())
    .toList();
}

export function getFetchingOffsetList(
  state: $TSFixMe,
  filterName: $TSFixMe,
  sortOrder: $TSFixMe,
): $TSFixMe {
  if (!filterHasCorrectSort(state, filterName, sortOrder)) return List();
  return getFilteredCollection(state, filterName)
    .get(CollectionKeys.FETCHING_OFFSETS, List())
    .toList();
}

const _memoizedFetchedAndFetchingOffsetList = memoize(
  (_filter, offsetList) => {
    return offsetList;
  },
  (filter, offsetList) => filter + offsetList.toJSON().join(','),
);

export const fetchedOffsetSelector = createSelector(
  (_state, filter) => filter,
  // @ts-expect-error TS(2554) FIXME: Expected 3 arguments, but got 2.
  (state, filter) => getFetchedOffsetList(state, filter),
  (filter, fetchedOffsetList) => {
    return _memoizedFetchedAndFetchingOffsetList(filter, fetchedOffsetList);
  },
);

export const fetchingOffsetSelector = createSelector(
  (_state, filter) => filter,
  // @ts-expect-error TS(2554) FIXME: Expected 3 arguments, but got 2.
  (state, filter) => getFetchingOffsetList(state, filter),
  (filter, fetchingOffsetList) => {
    return _memoizedFetchedAndFetchingOffsetList(filter, fetchingOffsetList);
  },
);

export function getFilteredCollectionTotalCount(state: $TSFixMe, filterName: $TSFixMe): $TSFixMe {
  return getFilteredCollection(state, filterName).get(CollectionKeys.TOTAL_COUNT);
}

export function getFilteredCollectionPreviousTotalCount(
  state: $TSFixMe,
  filterName: $TSFixMe,
): $TSFixMe {
  return getFilteredCollection(state, filterName).get(CollectionKeys.PREVIOUS_TOTAL_COUNT);
}

export function getFilteredCollectionCurrentOffset(
  state: $TSFixMe,
  filterName: $TSFixMe,
): $TSFixMe {
  return getFilteredCollection(state, filterName).get(CollectionKeys.CURRENT_OFFSET);
}

export function getFilteredCollectionItemsPreviousOffset(
  state: $TSFixMe,
  filterName: $TSFixMe,
): $TSFixMe {
  return getFilteredCollection(state, filterName).get(CollectionKeys.PREVIOUS_OFFSET);
}

export function getIsFilteredCollectionEmpty(state: $TSFixMe, filterName: $TSFixMe): $TSFixMe {
  return getFilteredCollectionItems(state, filterName).isEmpty();
}

export function getFilterShouldReload(state: $TSFixMe, filterName: $TSFixMe): $TSFixMe {
  return getFilteredCollection(state, filterName).get(CollectionKeys.SHOULD_RELOAD, false);
}

export function getFilterSort(state: $TSFixMe, filterName: $TSFixMe): $TSFixMe {
  return getFilteredCollection(state, filterName).get(CollectionKeys.SORT);
}

export function getFilterCurrentViewedOffset(state: $TSFixMe, filterName: $TSFixMe): $TSFixMe {
  return getFilteredCollection(state, filterName).get(CollectionKeys.CURRENT_VIEWED_OFFSET) || 0;
}

function _isCollectionCatalogitem(item: $TSFixMe): $TSFixMe {
  // Only collection (catalog) items have pandoraId set on them, stations items do not
  if (item) {
    return !!item.pandoraId;
  }
}

function _allFilteredRecentSortComparator(a: $TSFixMe, b: $TSFixMe): $TSFixMe {
  // Is this a collection item, or a station object?
  const aDate = _isCollectionCatalogitem(a)
    ? a.addedTime
    : D.getInDomain('playback.utils.getStationCreatedDate')(a);
  const bDate = _isCollectionCatalogitem(b)
    ? b.addedTime
    : D.getInDomain('playback.utils.getStationCreatedDate')(b);
  // @ts-expect-error TS(2362) FIXME: The left-hand side of an arithmetic operation must... Remove this comment to see the full error message
  return new Date(bDate) - new Date(aDate);
}

/**
 * Separate selector function is necessary to get items for All filter/view because it
 * combines catalog and stations items, which come from different places in different
 * formats.
 */
export const createGetAllFilteredCollectionItemsSelector = createSelector(
  state => D.getInDomain('playback.utils.getMyStationIds')(state),
  state => D.getStationEntities(state),
  state => getFilteredCollectionItems(state, FilterTypes.ALL),
  state => D.getAllAnnotations(state),
  state => getFilterSort(state, FilterTypes.ALL),
  (stationIds, stationEntities, catalogItems, annotations, sort) => {
    const stations = stationIds.map((stationId: $TSFixMe) => stationEntities.get(stationId));
    // !item is added so that the null entries (placeholders) are kept
    const items = catalogItems.concat(stations.valueSeq()).filter((item: $TSFixMe) => {
      return (
        !item ||
        (item.pandoraType !== D.PandoraTypes.ARTIST &&
          !D.isRemoved(annotations.get(item.pandoraId)))
      );
    });

    if (sort === D.MyMusicSorts.A_TO_Z) {
      return items.sortBy((item: $TSFixMe) => {
        if (item) {
          return _isCollectionCatalogitem(item)
            ? annotations.getIn([item.pandoraId, 'name'], '')
            : item.get('name', '');
        }
      }, D.alphabetizeComparator);
    }
    return items.sort(_allFilteredRecentSortComparator);
  },
);

export const filterCollectionAllItem = (
  item: $TSFixMe,
  annotations: $TSFixMe,
  stationEntities: $TSFixMe,
): $TSFixMe => {
  if (item) {
    const isCollectedStationType = item.pandoraType === D.PandoraTypes.USER_STATION;
    const isArtistType = item.pandoraType === D.PandoraTypes.ARTIST;
    const isRemoved = D.isRemoved(annotations.get(item.pandoraId));

    if (isCollectedStationType) {
      const stationId = item.pandoraId.split(':').pop();
      return !!stationEntities.get(stationId);
    } else if (isArtistType || isRemoved) {
      return false;
    }
  }

  return true;
};

export const mapCollectionAllItem = (item: $TSFixMe, stationEntities: $TSFixMe): $TSFixMe => {
  if (item) {
    const isCollectedStationType = item.pandoraType === D.PandoraTypes.USER_STATION;

    if (isCollectedStationType) {
      const stationId = item.pandoraId.split(':').pop();
      return stationEntities.get(stationId, null);
    }
  }

  return item;
};

export const selectCollectionAllItems = createSelector(
  state => getFilteredCollectionItems(state, FilterTypes.ALL),
  state => D.getAllAnnotations(state),
  state => D.getStationEntities(state),
  (collectedItems, annotations, stationEntities) => {
    return collectedItems
      .filter((item: $TSFixMe) => filterCollectionAllItem(item, annotations, stationEntities))
      .map((item: $TSFixMe) => mapCollectionAllItem(item, stationEntities));
  },
);

export const createGetFilteredCollectionItemsByTypeSelector = createSelector(
  (state, filter) => getFilteredCollectionItems(state, filter),
  state => D.getAllAnnotations(state),
  (catalogItems, annotations) => {
    return catalogItems.filter((item: $TSFixMe) => {
      return !item || !D.isRemoved(annotations.get(item.pandoraId));
    });
  },
);

/**
 * Separate selector function is necessary to get total count for All filter/view because it
 * combines catalog and stations items, which come from different places in different
 * formats.
 */
export const createGetAllFilteredCollectionTotalCountSelector = createSelector(
  state => D.getInDomain('playback.utils.getMyStationIds', 0)(state).size,
  state => getFilteredCollectionTotalCount(state, FilterTypes.ALL) || 0,
  state => D.Utils.TunerVars.collectionV7EndpointsEnabled(state),
  (stationItemsCount, otherItemsCount, collectionV7EndpointsEnabled) => {
    if (collectionV7EndpointsEnabled) {
      return otherItemsCount;
    }

    return stationItemsCount + otherItemsCount;
  },
);

export function getIsAllFilteredCollectionEmpty(state: $TSFixMe): $TSFixMe {
  return !createGetAllFilteredCollectionTotalCountSelector(state);
}

export function getArtistViewShouldReload(state: $TSFixMe): $TSFixMe {
  return getCollectionState(state).get(CollectionKeys.SHOULD_RELOAD_ARTIST_VIEW);
}

export function isPandoraIdInCollection(
  state: $TSFixMe,
  pandoraId: $TSFixMe,
  albumId = null,
): $TSFixMe {
  let inCollection = !!getCollectionItems(state).find((item: $TSFixMe) => {
    return item.pandoraId === pandoraId;
  });

  if (!inCollection && albumId) {
    inCollection = isPandoraIdInCollection(state, albumId);
  }
  if (!inCollection) {
    inCollection = D.getInDomain('playback.utils.isPandoraIdInMyStations', false)(state, pandoraId);
  }
  return inCollection;
}

export function getItemInCollectionForId(state: $TSFixMe, pandoraId: $TSFixMe): $TSFixMe {
  return getCollectionItems(state).find((item: $TSFixMe) => item.pandoraId === pandoraId);
}

export function getCollectionVersion(state: $TSFixMe): $TSFixMe {
  return getCollectionState(state).get(CollectionKeys.VERSION);
}

export function getCollectionPreviousVersion(state: $TSFixMe): $TSFixMe {
  return getCollectionState(state).get(CollectionKeys.PREVIOUS_VERSION);
}

export function hasCollectionUpdateSucceeded(action: $TSFixMe): $TSFixMe {
  return has(action.response, 'added') || has(action.response, 'removed');
}

export function getCollectionFetchedDataSuccess(state: $TSFixMe): $TSFixMe {
  return getCollectionState(state).get(CollectionKeys.FETCHED_DATA_SUCCESS_KEY);
}

export function isPlaylistAction(action: $TSFixMe): $TSFixMe {
  return (
    action.type === D.ApiActions.PLAYLISTS_CREATE_SUCCESS ||
    action.type === D.ApiActions.PLAYLISTS_EDIT_TRACKS_SUCCESS ||
    action.type === D.ApiActions.PLAYLISTS_SET_DETAILS_SUCCESS
  );
}

export function getCollectionCursor(state: $TSFixMe, sortedFilter = null): $TSFixMe {
  let path = [CollectionKeys.COLLECTION];
  if (sortedFilter) {
    // @ts-expect-error TS(2322) FIXME: Type '"SORTED_FILTERS"' is not assignable to type ... Remove this comment to see the full error message
    path = [CollectionKeys.SORTED_FILTERS, sortedFilter];
  }
  // @ts-expect-error TS(2345) FIXME: Argument of type '"CURSOR"' is not assignable to p... Remove this comment to see the full error message
  path.push(CollectionKeys.CURSOR);
  return getCollectionState(state).getIn(path, null);
}

export function _buildListItemProps({ items }: $TSFixMe, additionalItemProps = {}): $TSFixMe {
  // Here we normalize catalog items and station objects
  const toNormalize = items || List();
  return toNormalize
    .map((item: $TSFixMe, i: $TSFixMe) => {
      // Catalog items are just a plain object that contains:
      // pandoraId, pandoraType, addedTime and optionally albumPandoraId.
      // Other fields may rarely be set, but the above are the only ones we're
      // interested in right now.
      if (item && item.pandoraId) {
        return Object.assign(
          {
            id: item.pandoraId,
            isLazyLoaded: true,
            key: item.pandoraId,
          },
          item,
          additionalItemProps,
        );
        // Station items are simply the station object
      } else if (item) {
        const id = item.get('stationId');
        return Object.assign(
          {
            id,
            isLazyLoaded: (additionalItemProps as $TSFixMe).collectionV7EndpointsEnabled,
            key: id,
            pandoraType: D.PandoraTypes.STATION,
          },
          item,
          additionalItemProps,
        );
        // blank items
      }
      return {
        isPlaceholder: true,
        key: i,
        mainText: '',
      };
    })
    .toJSON();
}

export const createGetCollectionPropsSelector = createSelector(
  (state, props) => getFilteredCollectionItemsPreviousOffset(state, props.filter),
  (state, props) => {
    if (props.filter === FilterTypes.ALL) {
      return D.Utils.TunerVars.collectionV7EndpointsEnabled(state)
        ? selectCollectionAllItems(state)
        : createGetAllFilteredCollectionItemsSelector(state);
    }

    return createGetFilteredCollectionItemsByTypeSelector(state, props.filter);
  },
  (state, props) =>
    props.filter === FilterTypes.ALL &&
    // @ts-expect-error TS(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
    D.Utils.Route.isCollectionPath(D.Utils.Route.getCurrentPathnameFromState(state)),
  (_state, props) => props.activeSort,
  state => D.Utils.TunerVars.collectionV7EndpointsEnabled(state),
  (_previousOffset, items, isMixedWithOtherSources, activeSort, collectionV7EndpointsEnabled) => {
    return memoize(() =>
      _buildListItemProps(
        { items },
        {
          collectionV7EndpointsEnabled,
          isMixedWithOtherSources,
          sortOrder: activeSort,
        },
      ),
    );
  },
);

export function getOwnedPlaylistIds(state: $TSFixMe): $TSFixMe {
  const filtered = getCollectionItems(state).filter((item: $TSFixMe) => {
    // If no ownerId is present on a playlist item, the current listener is the owner
    return item.pandoraType === D.PandoraTypes.PLAYLIST && !item.ownerId;
  });
  return map(filtered.toJSON(), 'pandoraId');
}

export const getOwnedPlaylistIdsWithAnnotations = createSelector(
  state => getCollectionItems(state),
  state => D.getAllAnnotations(state),
  (collectionItems, allAnnotations) =>
    collectionItems.reduce((result: $TSFixMe, item: $TSFixMe) => {
      if (item.pandoraType === D.PandoraTypes.PLAYLIST && !item.ownerId) {
        item.annotations = allAnnotations.get(item.pandoraId, Map());
        result.unshift(item);
      }
      return result;
    }, []),
);

export function getArtistAnnotation(state: $TSFixMe, artistId: $TSFixMe): $TSFixMe {
  if (getCollectionState(state).get(CollectionKeys.ARTISTS_LIST).includes(artistId)) {
    return D.getInDomain('catalog.utils.getAnnotations')(state, artistId);
  }
  return null;
}

export function getArtistAnnotationsInCollection(
  state: $TSFixMe,
  artistAnnotations: $TSFixMe,
): $TSFixMe {
  const artistItems: $TSFixMe = [];
  artistAnnotations.forEach((artistAnnotation: $TSFixMe) => {
    const pandoraId = artistAnnotation.get('pandoraId');
    if (isPandoraIdInCollection(state, pandoraId)) {
      const item = getItemInCollectionForId(state, pandoraId);
      artistItems.push(
        Object.assign(
          {
            details: artistAnnotation,
            id: pandoraId,
            key: pandoraId,
            onArtistPage: true,
          },
          item,
        ),
      );
    }
  });
  return artistItems;
}

export function getArtistsItemsIdsList(state: $TSFixMe): $TSFixMe {
  return getCollectionState(state).get(CollectionKeys.ARTISTS_ITEMS_ID_LIST);
}

function buildArtistItemsIds(items: $TSFixMe): $TSFixMe {
  const artistItemsIds: $TSFixMe = [];
  if (items) {
    items.forEach((item: $TSFixMe) => {
      artistItemsIds.push(item.get('pandoraId'));
    });
  }
  return artistItemsIds;
}

export const createGetArtistItemsIdsSelector = createSelector(
  getArtistsItemsIdsList,
  buildArtistItemsIds,
);

const filterTypesToPandoraTypes = Object.freeze({
  [FilterTypes.ARTISTS]: 'AR',
  [FilterTypes.SONGS]: 'TR',
  [FilterTypes.ALBUMS]: 'AL',
  [FilterTypes.PLAYLISTS]: 'PL',
  [FilterTypes.PODCASTS]: 'PC',
  [FilterTypes.EPISODES]: 'PE',
});

const pandoraTypesToFilterTypes = invert(filterTypesToPandoraTypes);

export function getViewFromFilter(filterType: $TSFixMe): $TSFixMe {
  // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  return filterTypesToPandoraTypes[filterType];
}

export function getFilterFromView(view: $TSFixMe): $TSFixMe {
  return pandoraTypesToFilterTypes[view];
}

export function getAddedHeaderActiveUrl(filter: $TSFixMe): $TSFixMe {
  return '/' + filter;
}

export function getCurrentFilter(state: $TSFixMe): $TSFixMe {
  return getCollectionState(state).get(CollectionKeys.CURRENT_FILTER);
}

export function getIsCurrentlySyncing(state: $TSFixMe): $TSFixMe {
  return getCollectionState(state).get(CollectionKeys.IS_CURRENTLY_SYNCING);
}

export function getCurrentMyCollectionPath(state: $TSFixMe): $TSFixMe {
  if (getCurrentFilter(state) === FilterTypes.ALL) {
    return D.getCollectionPath();
  }
  return D.getCollectionPath() + '/' + getCurrentFilter(state);
}

export function isStationOrPodcastItem(item: $TSFixMe): $TSFixMe {
  if (!item || !item.pandoraType) return false;
  const stationAndPodcastPandoraTypes = [
    D.Constants.SourceTypes.PandoraTypes.STATION,
    D.Constants.SourceTypes.PandoraTypes.PODCAST,
    D.Constants.SourceTypes.PandoraTypes.EPISODE,
  ];
  return stationAndPodcastPandoraTypes.includes(item.pandoraType);
}

/**
 * Given a playlist details/annotations, checks if playlist is not a locked station-linked playlist.
 * Playlists also have a linkedType field that could be checked, but checking the source ID seemed more stable.
 *
 * @param {Map} details - annotations or entity details for playlist item
 * @returns {boolean} true for unlocked playlist, false for locked
 */
export function isUnlockedPlaylist(details: $TSFixMe): $TSFixMe {
  return !(details && details.get('linkedSourceId') && details.get('unlocked') === false);
}

/**
 * This filter insures locked playlists are not displayed.
 * Station linked automated playlists are locked by default until the listener tracks a coach mark.
 *
 * @param {Immutable.Map} allAnnotations - all available annotations
 * @param {object} item - item in original list to be filtered
 * @returns {boolean} true to keep item in list, false to filter out
 */
function lockedPlaylistFilter(allAnnotations: $TSFixMe, item: $TSFixMe): $TSFixMe {
  if (!item || item.pandoraType !== D.PandoraTypes.PLAYLIST) {
    return true;
  }
  const details = allAnnotations.get(item.pandoraId);
  return isUnlockedPlaylist(details);
}

export const filterLockedPlaylistsSelector = createSelector(
  state => D.getAllAnnotations(state),
  (_state, items) => items,
  (allAnnotations, items) => {
    return items.filter((item: $TSFixMe) => lockedPlaylistFilter(allAnnotations, item));
  },
);

export const filterStationsAndPodcastsSelector = createSelector(
  (_state, items) => items,
  items => {
    return items.filter((item: $TSFixMe) => isStationOrPodcastItem(item));
  },
);

export function getBrowseDeepLevelSection(state: $TSFixMe, requestId: $TSFixMe): $TSFixMe {
  return D.getInDomain('discovery.selectors.getBrowseDeepLevelDirectoryByRequestId', null)(
    state,
    requestId,
  );
}

export const recommendationPlaceholders = ((): $TSFixMe => {
  const placeholders = [];
  for (let i = 0; i < RECOMMENDATIONS_MAX_LIST_SIZE; i++) {
    placeholders.push({ blankItem: true, id: null, pandoraType: null });
  }
  return placeholders;
})();

export const getRecommendedPodcastPrograms = createSelector(
  state => getBrowseDeepLevelSection(state, D.Constants.Podcasts.RECOMMENDED_PODCAST_DIRECTORY_ID),
  section => {
    const recommendedPodcasts = get(section, 'subsections[0].items', []);
    return recommendedPodcasts.slice(0, RECOMMENDATIONS_MAX_LIST_SIZE).map((podcast: $TSFixMe) => {
      return {
        id: get(podcast, 'pandoraId', null),
        pandoraType: D.Constants.SourceTypes.PandoraTypes.PODCAST,
      };
    });
  },
);

export const getRecommendedPodcastEpisodes = createSelector(
  state => getBrowseDeepLevelSection(state, D.Constants.Podcasts.RECOMMENDED_EPISODE_DIRECTORY_ID),
  section => {
    const recommendedPodcasts = get(section, 'items', []);
    return recommendedPodcasts.slice(0, RECOMMENDATIONS_MAX_LIST_SIZE).map((podcast: $TSFixMe) => {
      return {
        id: get(podcast, 'pandoraId', null),
        pandoraType: D.Constants.SourceTypes.PandoraTypes.EPISODE,
      };
    });
  },
);

export const getRecommendedGenreStations = createSelector(
  state => D.getInDomain('playback.utils.getGenreStationRecommendations')(state),
  genres => {
    const recommendedGenres = genres && genres.size ? genres.toJS() : [];
    return recommendedGenres.slice(0, RECOMMENDATIONS_MAX_LIST_SIZE).map((genreId: $TSFixMe) => {
      return {
        id: genreId,
        pandoraType: D.Constants.SourceTypes.PandoraTypes.GENRE,
      };
    });
  },
);

export const getRecommendedPodcastsAndGenreStations = createSelector(
  getRecommendedPodcastPrograms,
  getRecommendedPodcastEpisodes,
  getRecommendedGenreStations,
  (podcastPrograms, podcastEpisodes, genreStations) => {
    const recommendations = zip(podcastPrograms, podcastEpisodes, genreStations);
    const flattened = flatMap(recommendations, recommendation =>
      recommendation.filter(item => item),
    );
    return flattened.slice(0, RECOMMENDATIONS_MAX_LIST_SIZE);
  },
);

function lockedAndUneditablePlaylistFilter(allAnnotations: $TSFixMe, item: $TSFixMe): $TSFixMe {
  if (!item || item.pandoraType !== D.PandoraTypes.PLAYLIST) {
    return true;
  }
  const details = allAnnotations.get(item.pandoraId);
  if (!details) {
    return true; // assume unlocked and owned by default, allowing user to scroll into view and trigger api call
  }
  const isEditable = D.getInDomain('playback.utils.isPlaylistEditable', false)(details);
  return isEditable && lockedPlaylistFilter(allAnnotations, item);
}

export const filterLockedAndUneditablePlaylistsSelector = createSelector(
  state => D.getAllAnnotations(state),
  state => D.getCurrentListenerIdFromState(state as $TSFixMe),
  (_state, items) => items,
  (allAnnotations, _currentListenerId, items) => {
    return items.filter((item: $TSFixMe) =>
      lockedAndUneditablePlaylistFilter(allAnnotations, item),
    );
  },
);

export const externalUtils = {
  collectionHasItems,
  createGetArtistItemsIdsSelector,
  createGetCollectionPropsSelector,
  fetchedOffsetSelector,
  fetchingOffsetSelector,
  getArtistAnnotation,
  getArtistAnnotationsInCollection,
  getArtistViewShouldReload,
  getCollectedAlbumTracks,
  getCollectionCursor,
  getCollectionFetchedDataSuccess,
  getCollectionVersion,
  getCurrentFilter,
  getCurrentMyCollectionPath,
  getFetchedOffsetList,
  getFetchingOffsetList,
  getFilterCurrentViewedOffset,
  getFilteredCollection,
  getFilteredCollectionCurrentOffset,
  getFilteredCollectionItems,
  getFilteredCollectionTotalCount,
  getFilterShouldReload,
  getFilterSort,
  getIndexOfTrack,
  getIsCurrentlySyncing,
  getIsFilteredCollectionEmpty,
  getOwnedPlaylistIds,
  getOwnedPlaylistIdsWithAnnotations,
  getRecentlyPlayedPodcasts,
  getSongPandoraIds,
  isPandoraIdInCollection,
  sortTrackIdsByTrackNumber,
};
